import {
    getInternetDiscounts,
    getInternetOfferDetail,
    getInternetPabAdjustmentAmount,
    getInternetPriceBreakdown,
    getInternetPricing,
    internetMobilityDiscountEligible
} from './internetPricing';
import { getTvPriceBreakdown, TV_GIFT_OFFER } from './tvPricing';
import { formatMobilityLinePricingNote, getMobilityTotal } from './mobilityPricing';
import { formatMoney } from './formatters';

export function calculateBundleQuote({
    builder,
    mobilityDiscountActive,
    selectedAddons,
    activeTvGiftAdjustment,
    activeTvPriceAdjustment,
    selectedInternet,
    selectedLongDistance,
    selectedMobility,
    selectedPhone,
    selectedSecurity,
    selectedTv,
    extraTvBoxPrice: EXTRA_TV_BOX_PRICE = 10,
    pvrPrice: PVR_PRICE = 10,
    billCreditAmount: BILL_CREDIT_AMOUNT = 50
}) {
    const items = [];
    const discounts = [];
    const notes = [];
    let internetPriceBreakdown = null;
    let tvPriceBreakdown = null;
    let subtotal = 0;
    let homeServicesSubtotal = 0;

    if (selectedInternet) {
        const internetPricing = getInternetPricing(selectedInternet, mobilityDiscountActive, builder.eppEnabled, builder.pabIncluded);
        const internetDiscountSummary = getInternetOfferDetail(selectedInternet, builder.pabIncluded, mobilityDiscountActive, builder.eppEnabled);
        internetPriceBreakdown = getInternetPriceBreakdown(selectedInternet, internetPricing, builder.pabIncluded, mobilityDiscountActive, builder.eppEnabled);

        subtotal += internetPricing.belowLine;
        homeServicesSubtotal += internetPricing.belowLine;
        items.push({
            section: 'Internet',
            label: `Internet ${selectedInternet.name}`,
            amount: internetPricing.belowLine,
            detail: internetDiscountSummary
        });

        getInternetDiscounts(selectedInternet, mobilityDiscountActive, builder.eppEnabled).forEach((discount) => {
            discounts.push(discount);
        });

        if (!builder.pabIncluded) {
            notes.push(`PAB Auto Pay discount is not included, so Internet is ${formatMoney(getInternetPabAdjustmentAmount(selectedInternet))} higher.`);
        }

        (selectedInternet.notes || []).forEach((note) => notes.push(note));
    }

    if (selectedTv) {
        const selectedPvrPrice = Number(selectedTv.pvrPrice ?? PVR_PRICE);
        const pvrStatus = selectedTv.pvrMode === 'included'
            ? 'PVR included'
            : selectedTv.pvrMode === 'optional' && builder.addPvr
                ? `PVR added: +${formatMoney(selectedPvrPrice)}`
                : selectedTv.pvrMode === 'optional'
                    ? 'PVR not selected'
                    : 'PVR not included';
        const isRecommendedTv = selectedTv.source === 'tv-recommender';
        const tvAddOnCount = Number(selectedTv.addOnCount || 0);
        tvPriceBreakdown = getTvPriceBreakdown(
            selectedTv,
            builder.eppEnabled,
            activeTvPriceAdjustment,
            activeTvGiftAdjustment
        );

        subtotal += tvPriceBreakdown.finalTotal;
        homeServicesSubtotal += tvPriceBreakdown.finalTotal;
        items.push({
            section: 'TV',
            label: selectedTv.name,
            amount: tvPriceBreakdown.finalTotal,
            detail: [
                isRecommendedTv
                    ? `V2 recommendation base total ${formatMoney(tvPriceBreakdown.basePrice)}${tvAddOnCount > 0 ? ` including ${tvAddOnCount} extra theme pack${tvAddOnCount === 1 ? '' : 's'}` : ''}`
                    : `Base TV price ${formatMoney(tvPriceBreakdown.basePrice)}`,
                tvPriceBreakdown.adjustmentAmount > 0
                    ? `${tvPriceBreakdown.adjustmentLabel || 'Internet TV adjustment'} +${formatMoney(tvPriceBreakdown.adjustmentAmount)}`
                    : null,
                tvPriceBreakdown.giftAdjustmentAmount > 0
                    ? `${tvPriceBreakdown.giftAdjustmentLabel || TV_GIFT_OFFER.label} +${formatMoney(tvPriceBreakdown.giftAdjustmentAmount)}`
                    : null,
                tvPriceBreakdown.eppDiscount > 0 ? 'After EPP discount' : 'No EPP discount',
                selectedTv.quoteSummaryNote || `First TV box included/free; ${pvrStatus}`
            ].filter(Boolean).join('; ')
        });

        if (selectedTv.pvrIncluded) {
            items.push({ section: 'TV', label: 'PVR', amount: 0, detail: 'PVR included' });
        } else if (selectedTv.pvrMode === 'optional' && builder.addPvr) {
            subtotal += selectedPvrPrice;
            items.push({ section: 'TV', label: 'PVR', amount: selectedPvrPrice, detail: `PVR added: +${formatMoney(selectedPvrPrice)}` });
        }

        if (builder.extraBoxes > 0) {
            const boxCost = builder.extraBoxes * EXTRA_TV_BOX_PRICE;
            subtotal += boxCost;
            items.push({ section: 'TV', label: `Extra TV box x ${builder.extraBoxes}`, amount: boxCost, detail: `${formatMoney(EXTRA_TV_BOX_PRICE)}/box` });
        }

        if (tvPriceBreakdown.eppDiscount > 0) {
            discounts.push({ label: 'EPP closing tool - TV', amount: tvPriceBreakdown.eppDiscount });
        }

        notes.push('All TV orders include free pro install valued at $100.');
    }

    if (selectedPhone) {
        subtotal += selectedPhone.price;
        homeServicesSubtotal += selectedPhone.price;
        items.push({
            section: 'Home Phone',
            label: selectedPhone.name,
            amount: selectedPhone.price,
            detail: selectedPhone.cardDescription || `Bundle promo including required 911 fees; original ${formatMoney(selectedPhone.originalPrice)}; ${selectedPhone.features}`
        });
        notes.push('Home Phone is for new activations only and must be activated with Internet or Internet + TV.');
    }

    if (selectedPhone) {
        selectedLongDistance.forEach((addon) => {
            if (addon.price > 0) {
                subtotal += addon.price;
            }

            items.push({
                section: 'Home Phone',
                label: addon.name,
                amount: addon.price,
                detail: addon.note || 'Long-distance add-on'
            });
        });
    }

    if (builder.mobilityState === 'adding' && selectedMobility) {
        const mobilityTotal = getMobilityTotal(selectedMobility, builder.mobilityLines, builder.pabIncluded);
        const mobilityLineCount = Math.max(1, Number(builder.mobilityLines) || 1);
        const mobilityLinePricingNote = selectedMobility.linePricingNote || formatMobilityLinePricingNote(selectedMobility.linePricing);
        subtotal += mobilityTotal;
        items.push({
            section: 'New Mobility Line',
            label: `${selectedMobility.name} x ${mobilityLineCount}`,
            amount: mobilityTotal,
            detail: [
                `${mobilityLineCount} line${mobilityLineCount === 1 ? '' : 's'}`,
                mobilityLinePricingNote,
                selectedMobility.type,
                builder.pabIncluded ? 'PAB included' : 'PAB not included: +$10/line',
                'Separate flow / quote-only beta'
            ].filter(Boolean).join('; ')
        });

        if (!builder.pabIncluded) {
            notes.push('PAB Auto Pay discount is not included, so selected mobility pricing is $10/line higher.');
        }
    }

    if (selectedSecurity) {
        subtotal += selectedSecurity.price;
        items.push({
            section: 'Smart Home Security',
            label: selectedSecurity.name,
            amount: selectedSecurity.price,
            detail: [
                selectedSecurity.term,
                selectedSecurity.devices,
                'Separate flow / quote-only beta'
            ].filter(Boolean).join('; ')
        });
        notes.push('Security includes free installation, requires a 3-year contract, and is installed separately from internet.');
    }

    selectedAddons.forEach((addon) => {
        const quantity = addon.quantity || 1;
        const addonTotal = addon.price * quantity;
        subtotal += addonTotal;
        items.push({
            section: 'Add-ons',
            label: addon.allowQuantity ? `${addon.name} x ${quantity}` : addon.name,
            amount: addonTotal,
            detail: [addon.note || addon.group, 'Quote-only beta'].filter(Boolean).join('; ')
        });
    });

    const billCredit = selectedInternet && internetMobilityDiscountEligible(selectedInternet) && mobilityDiscountActive && (selectedTv || selectedPhone) ? BILL_CREDIT_AMOUNT : 0;
    const monthlySubtotal = Math.max(subtotal, 0);
    const homeServicesMonthlySubtotal = Math.max(homeServicesSubtotal, 0);
    const currentMonthlyBill = Number(builder.currentMonthlyBill);
    const hasCurrentMonthlyBill = Number.isFinite(currentMonthlyBill) && currentMonthlyBill > 0;
    const monthlyDifference = hasCurrentMonthlyBill ? currentMonthlyBill - monthlySubtotal : 0;
    const twoYearDifference = monthlyDifference * 24;
    const savings = hasCurrentMonthlyBill ? {
        currentMonthlyBill,
        monthlyDifference,
        twoYearDifference,
        twoYearImpactWithCredit: twoYearDifference + billCredit,
        status: monthlyDifference > 0.005 ? 'saves' : monthlyDifference < -0.005 ? 'more' : 'similar'
    } : null;

    return {
        items,
        discounts,
        notes: [...new Set(notes)],
        monthlySubtotal,
        homeServicesSubtotal: homeServicesMonthlySubtotal,
        quoteOnlySubtotal: Math.max(monthlySubtotal - homeServicesMonthlySubtotal, 0),
        billCredit,
        savings,
        internetPriceBreakdown,
        tvPriceBreakdown,
        tvSelectionSummary: selectedTv?.source === 'tv-recommender' ? selectedTv.recommendationSummary : null
    };
}
